import { useEffect, useState } from "react";
import api from "../utils/axios";
import Swal from "sweetalert2";

const BackOffice = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "backoffice",
    amount: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [debitAmounts, setDebitAmounts] = useState({}); // Object to store debit amounts per admin
  const [creditAmounts, setCreditAmounts] = useState({}); // Object to store credit amounts per admin

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async (searchVal = "", startVal = "", endVal = "") => {
    try {
      const response = await api.get("/list", {
        params: {
          search: searchVal || undefined,
          startDate: startVal || undefined,
          endDate: endVal || undefined,
        },
      });
      setAdmins(response?.data);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const payload = {
      ...formData,
      email: `${formData.username.trim()}@gmail.com`,
    };

    try {
      const response = await api.post("/create", payload);
      Swal.fire({
        icon: "success",
        title: "Back Office Created",
        text: response.data.message || "Back office account created successfully.",
      });
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "backoffice",
        amount: "",
      });
      setShowModal(false);
      setShowPassword(false);
      fetchAdmins(searchTerm, startDate, endDate);
    } catch (error) {
      const errMsg = error.response?.data?.message || "Failed to create back office account";
      setMessage(errMsg);
      Swal.fire({
        icon: "error",
        title: "Creation Failed",
        text: errMsg,
      });
    }
    setLoading(false);
  };

  const handleCreditOrDebit = async (uid, type) => {
    const amount = type === "credit" ? creditAmounts[uid] : debitAmounts[uid];

    if (!amount || isNaN(amount)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Please enter a valid numeric amount.",
      });
      return;
    }

    try {
      const response = await api.post("/adminUpdateSubAdminCredit", {
        id: uid,
        amount,
        type,
      });

      Swal.fire({
        icon: "success",
        title: "Wallet Updated",
        text: response.data.message || `backoffice ${type} successful.`,
      });

      fetchAdmins(searchTerm, startDate, endDate); // Refresh the admin list
      setDebitAmounts({ ...debitAmounts, [uid]: "" });
      setCreditAmounts({ ...creditAmounts, [uid]: "" });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || "Failed to update wallet.",
      });
    }
  };

  const handleAmountChange = (uid, value, type) => {
    if (type === "credit") {
      setCreditAmounts({ ...creditAmounts, [uid]: value });
    } else {
      setDebitAmounts({ ...debitAmounts, [uid]: value });
    }
  };

  const handleToggleStatus = async (uid, currentStatus) => {
    const is_active = !currentStatus;
    const action = is_active ? "unblock" : "block";
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Account`,
      text: `Are you sure you want to ${action} this account?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await api.post("/updateStatus", { id: uid, is_active });
        Swal.fire({ icon: "success", title: "Success", text: `Account ${action}ed successfully.` });
        fetchAdmins(searchTerm, startDate, endDate);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || `Failed to ${action} account.`,
        });
      }
    }
  };

  const handleEditPassword = async (uid) => {
    const { value: newPassword } = await Swal.fire({
      title: "Change Password",
      input: "password",
      inputLabel: "New Password",
      inputPlaceholder: "Enter new password",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return "Password is required";
        }
      },
    });
    if (newPassword) {
      try {
        await api.post("/changeChildPassword", { id: uid, newPassword });
        Swal.fire({ icon: "success", title: "Success", text: "Password updated successfully." });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to update password.",
        });
      }
    }
  };


  return (
    <div className="min-h-screen space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        {/* <div>
          <h1 className="text-2xl font-bold text-gray-800">Back Office Management</h1>
          <p className="text-sm text-gray-500">Manage back office admin accounts and credit balances.</p>
        </div> */}

      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-4 mb-6 bg-white p-4 rounded-xl border">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search</label>
          <input
            className="rounded border px-4 py-1.5 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-green-500"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                fetchAdmins(searchTerm, startDate, endDate);
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From Date</label>
          <input
            type="date"
            className="rounded border px-4 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To Date</label>
          <input
            type="date"
            className="rounded border px-4 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="flex items-end gap-2 h-14 pt-5">
          <button
            onClick={() => {
              fetchAdmins(searchTerm, startDate, endDate);
            }}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold text-sm transition"
          >
            Search
          </button>
          <button
            onClick={() => {
              setSearchTerm("");
              setStartDate("");
              setEndDate("");
              fetchAdmins("", "", "");
            }}
            className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-semibold text-sm transition"
          >
            Clear
          </button>

        </div>
        <div className="w-full">
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition"
          >
            + Create Back Office
          </button>
        </div>
      </div>

      {/* Admin Table */}
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm truncate">
          <thead className="bg-gray-100 ">

            <tr>
              <th className="py-3 px-6 border-b">S.No.</th>
              <th className="py-3 px-6 border-b">Name</th>
              <th className="py-3 px-6 border-b">Password</th>
              <th className="py-3 px-6 border-b">Available Credit</th>
              <th className="py-3 px-6 border-b">Credits with Agents</th>
              <th className="py-3 px-6 border-b">Credits with Players</th>
              <th className="py-3 px-6 border-b">Total Credit</th>

              <th className="py-3 px-4 border-b">Role</th>
              <th className="py-3 px-4 border-b">Date</th>
              <th className="py-3 px-4 border-b text-center">Status</th>
              <th className="py-3 px-4 border-b text-center">Actions</th>
              <th className="py-3 px-2 border-b text-center">Debit Action</th>
              <th className="py-3 px-2 border-b text-center">Credit Action</th>
            </tr>
          </thead>
          <tbody>
            {admins?.data?.map((item, index) => (
              <tr key={item.id} className="hover:bg-gray-50 transition border-t">
                <td className="py-4 px-6 border-b">{index + 1}</td>

                <td className="py-4 px-6 border-b font-medium text-gray-800">{item.username}</td>
                <td className="py-4 px-6 border-b font-medium">{item.password2 || "N/A"}</td>
                <td className="py-4 px-6 border-b font-semibold text-green-600">
                  {Number(item.available_credit || 0).toLocaleString()}
                </td>
                <td className="py-4 px-6 border-b font-semibold text-green-600">
                  {Number(item.credits_with_agents || 0).toLocaleString()}
                </td>
                <td className="py-4 px-6 border-b font-semibold text-green-600">
                  {Number(item.credits_with_players || 0).toLocaleString()}
                </td>
                <td className="py-4 px-6 border-b font-semibold text-green-600">
                  {Number(item.total_credit || 0).toLocaleString()}
                </td>
                <td className="py-4 px-6 border-b font-medium capitalize">{item.role}</td>
                <td className="py-4 px-6 border-b text-gray-400">
                  {item.created_at ? new Date(item.created_at).toLocaleDateString() : "N/A"}
                </td>
                <td className="py-4 px-4 border-b text-center">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${item.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {item.is_active ? "Active" : "Blocked"}
                  </span>
                </td>
                <td className="py-4 px-4 border-b text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <button
                      onClick={() => handleToggleStatus(item.id, item.is_active)}
                      className={`text-xs py-1.5 px-3 rounded-lg font-semibold transition ${item.is_active ? "bg-red-500 hover:bg-red-600 text-white" : "bg-green-600 hover:bg-green-700 text-white"}`}
                    >
                      {item.is_active ? "Block" : "Unblock"}
                    </button>
                    <button
                      onClick={() => handleEditPassword(item.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs py-1.5 px-3 rounded-lg transition font-semibold"
                    >
                      Password
                    </button>
                  </div>
                </td>

                {/* Debit */}
                <td className="py-4 px-2 border-b">
                  <div className="flex items-center justify-center -space-x-px">
                    <input
                      type="text"
                      value={debitAmounts[item.id] || ""}
                      onChange={(e) =>
                        handleAmountChange(item.id, e.target.value, "debit")
                      }
                      placeholder="Amount"
                      className="p-1.5 border rounded-l-lg w-20 text-xs text-center focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                    <button
                      onClick={() => handleCreditOrDebit(item.id, "debit")}
                      className="bg-red-500 text-white text-xs py-1.5 px-3 rounded-r-lg hover:bg-red-600 transition font-semibold"
                    >
                      Withdrawal
                    </button>
                  </div>
                </td>

                {/* Credit */}
                <td className="py-4 px-2 border-b">
                  <div className="flex items-center justify-center -space-x-px">
                    <input
                      type="text"
                      value={creditAmounts[item.id] || ""}
                      onChange={(e) =>
                        handleAmountChange(item.id, e.target.value, "credit")
                      }
                      placeholder="Amount"
                      className="p-1.5 border rounded-l-lg w-20 text-xs text-center focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                    <button
                      onClick={() => handleCreditOrDebit(item.id, "credit")}
                      className="bg-green-600 text-white text-xs py-1.5 px-3 rounded-r-lg hover:bg-green-700 transition font-semibold"
                    >
                      Deposit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Back Office Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg border w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Create Back Office</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder="Enter username"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                />
              </div>
              <div className="hidden">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter secure password"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                />
                <div className="flex items-center gap-2 mt-1.5">
                  <input
                    type="checkbox"
                    id="showPassword"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                  />
                  <label htmlFor="showPassword" className="text-xs text-gray-600 cursor-pointer select-none font-medium">
                    Show Password
                  </label>
                </div>
              </div>
              <div className="hidden">
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                >
                  <option value="backoffice" selected>Backoffice</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credits (Optional)</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Initial credits amount"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition"
                >
                  {loading ? "Creating..." : "Create"}
                </button>
              </div>
              {message && <p className="text-center text-sm text-red-500 mt-2">{message}</p>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackOffice;
